import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import {
  listManagedCdqNotes,
  listManagedLoanNotes,
  ManagedLeadNotesResponse,
  ManagedLeadProductPath,
} from '../../../services/api/managed-leads/managedLeads';
import {
  MANAGED_LEAD_QUERY_OPTIONS,
  managedLeadNotesQueryKey,
} from '../managedLeadQueryKeys';

const NOTES_PAGE_SIZE = 25 as const;

const listNotesForProduct = (
  product: ManagedLeadProductPath,
  dealId: string,
  params: { page: number; limit: 25 }
): Promise<ManagedLeadNotesResponse> =>
  product === 'prestiti'
    ? listManagedLoanNotes(dealId, params)
    : listManagedCdqNotes(dealId, params);

/**
 * Notes of a managed lead, newest first. The detail page owns this state so an
 * update that invalidates the Notes is read again even while the Notes modal
 * is closed.
 */
const useManagedLeadNotes = (
  product: ManagedLeadProductPath,
  dealId: string
) => {
  const [page, setPage] = useState(1);
  const query = useQuery({
    ...MANAGED_LEAD_QUERY_OPTIONS,
    queryKey: [...managedLeadNotesQueryKey(product, dealId), page],
    queryFn: () =>
      listNotesForProduct(product, dealId, {
        page,
        limit: NOTES_PAGE_SIZE,
      }),
    enabled: dealId.length > 0,
  });
  const notes = useMemo(
    () =>
      [...(query.data?.results ?? [])].sort((left, right) => {
        const leftTime = Date.parse(left.createdAt ?? '') || 0;
        const rightTime = Date.parse(right.createdAt ?? '') || 0;
        return rightTime - leftTime;
      }),
    [query.data?.results]
  );

  return { page, setPage, query, notes };
};

export type ManagedLeadNotesState = ReturnType<typeof useManagedLeadNotes>;

export default useManagedLeadNotes;
